import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PageContent,
  Container,
  Button,
  Modal,
  FormActions,
  FormNotice,
  FormSection,
  FormSummary,
  FormSummaryHeading,
  FormSummaryMeta,
  StatusBadge,
} from 'components';
import { checkPermissions, globalPropTypes } from 'helpers';
import { formatDate, formatDateTime } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadIpmaMeetingApplicationCreator,
  decideIpmaMeetingApplicationCreator,
  chairDecideIpmaMeetingApplicationCreator,
  removeIpmaMeetingApplicationCreator,
} from 'store/actionsCreators';
import { describeSubmitter } from 'containers/ma/Meetings/Application/submitter';
import { flagKeys } from 'containers/ma/Meetings/Application/flag';
import Questionnaire from 'containers/ma/Meetings/Application/Questionnaire';
import StatusHistory from 'containers/ma/Meetings/Application/StatusHistory';
import { SummaryList } from 'containers/ma/Meetings/Application/Application.styled';
import { ReasonInput, ReasonLabel } from './Application.styled';

/**
 * The reason of a decision.
 *
 * Uncontrolled and cut off from the state of the screen: the modal rebuilds its
 * content whenever the screen re-renders, so a controlled field would lose the
 * focus after every keystroke. The value is handed over through a ref instead.
 */
const ReasonField = ({ label, placeholder, onChange }) => (
  <>
    <ReasonLabel>{label}</ReasonLabel>
    <ReasonInput
      rows={4}
      placeholder={placeholder}
      onChange={(event) => onChange(event.target.value)}
    />
  </>
);

ReasonField.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};

/**
 * One application of a Member Association, read only, as the green profile sees
 * it: who applied, the answers they gave, whether the proxy they named
 * confirmed the claim, and the accept / reject decision with its reason.
 */
const Application = ({
  meetingType,
  data,
  userPermissions,
  loadApplication,
  decideApplication,
  chairDecideApplication,
  clearApplication,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { applicationId } = useParams();

  // null while no decision is being confirmed, true / false once the user
  // picked accept or reject in the actions below.
  const [decision, setDecision] = useState(null);
  // The chair step of an observer application, told apart from the final one.
  const [chairStep, setChairStep] = useState(false);
  const reason = useRef('');

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    checkPermissions({ roles: [`ipma_meeting_view_${meetingType}`] }, userPermissions || []),
    '/ipma',
    'page.ipma.meetings.accessDenied',
  );

  useEffect(() => {
    if (!allowed) return undefined;

    loadApplication({ applicationId });

    return () => clearApplication();
  }, [applicationId, allowed]);

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  if (!data) {
    return <PageContent />;
  }

  const { meeting, application, memberAssociation, applicant, proxy, answers } = data;
  const backPath = `/ipma/meetings/${meetingType}`;

  const openDecision = (accept, chair = false) => {
    reason.current = '';
    setChairStep(chair);
    setDecision(accept);
  };

  const confirmDecision = () => {
    const payload = {
      applicationId: application.id,
      meetingType,
      accept: decision,
      reason: reason.current,
    };

    if (chairStep) {
      chairDecideApplication(payload, history);
    } else {
      decideApplication(payload, history);
    }

    setDecision(null);
  };

  const confirmHeading = () => {
    if (chairStep) {
      return decision
        ? t('page.ipma.meetings.application.confirmChairApprove')
        : t('page.ipma.meetings.application.confirmChairReject');
    }

    return decision
      ? t('page.ipma.meetings.application.confirmAccept')
      : t('page.ipma.meetings.application.confirmReject');
  };

  const proxyDisplay = proxy
    ? {
        label: proxy.country ? `${proxy.country} - ${proxy.name}` : proxy.name,
        approved: proxy.approved,
      }
    : null;

  return (
    <PageContent>
      <Container>
        <FormSummary>
          <div>
            <FormSummaryHeading>{meeting.name}</FormSummaryHeading>
            <FormSummaryMeta>
              {t('page.ma.meetings.application.meetingSummary', {
                date: formatDate(meeting.date),
                delivery: t(
                  meeting.onsite
                    ? 'page.ma.meetings.application.onsite'
                    : 'page.ma.meetings.application.online',
                ),
              })}
            </FormSummaryMeta>
          </div>
          <StatusBadge status={application.status}>{application.statusLabel}</StatusBadge>
        </FormSummary>

        <FormSection title={t('page.ipma.meetings.application.applicantSection')}>
          <SummaryList>
            <dt>{t('page.ipma.meetings.list.memberAssociation')}</dt>
            <dd>{memberAssociation ? memberAssociation.name : '-'}</dd>
            <dt>{t('page.ipma.meetings.application.country')}</dt>
            <dd>
              {memberAssociation && memberAssociation.country ? memberAssociation.country : '-'}
            </dd>
            <dt>{t('page.ipma.meetings.application.applicant')}</dt>
            <dd>{applicant ? `${applicant.name} (${applicant.email})` : '-'}</dd>
            <dt>{t('page.ipma.meetings.application.submittedBy')}</dt>
            <dd>{describeSubmitter(data.submittedBy) || '-'}</dd>
            <dt>{t('page.ipma.meetings.application.submittedAt')}</dt>
            <dd>{formatDateTime(application.submittedAt) || '-'}</dd>
            <dt>{t(flagKeys(data.flag).column)}</dt>
            <dd>{t(data.verified ? flagKeys(data.flag).onLong : flagKeys(data.flag).offLong)}</dd>
          </SummaryList>
        </FormSection>

        <Questionnaire
          meeting={meeting}
          answers={answers}
          disabled
          proxyDisplay={proxyDisplay}
          votingRepresentative={null}
          observer={!!data.observer}
        />

        {data.awaitsChair && !data.chairDecidable ? (
          <FormNotice type="info">{t('page.ipma.meetings.application.awaitsChair')}</FormNotice>
        ) : null}

        <FormActions>
          {data.chairDecidable && (
            <>
              <Button
                type="button"
                style={{ width: 'auto' }}
                onClick={() => openDecision(true, true)}
              >
                {t('page.ipma.meetings.application.chairApprove')}
              </Button>
              <Button
                type="button"
                secondary
                style={{ width: 'auto' }}
                onClick={() => openDecision(false, true)}
              >
                {t('page.ipma.meetings.application.chairReject')}
              </Button>
            </>
          )}
          {data.decidable && (
            <>
              <Button type="button" style={{ width: 'auto' }} onClick={() => openDecision(true)}>
                {t('page.ipma.meetings.application.accept')}
              </Button>
              <Button
                type="button"
                secondary
                style={{ width: 'auto' }}
                onClick={() => openDecision(false)}
              >
                {t('page.ipma.meetings.application.reject')}
              </Button>
            </>
          )}
          <Button
            type="button"
            secondary
            style={{ width: 'auto' }}
            onClick={() => history.push(backPath)}
          >
            {t('page.ipma.meetings.application.back')}
          </Button>
        </FormActions>

        <StatusHistory items={data.statusHistory} />
      </Container>

      <Modal
        heading={confirmHeading()}
        description={
          <ReasonField
            label={t('page.ipma.meetings.application.reasonLabel')}
            placeholder={t('page.ipma.meetings.application.reasonPlaceholder')}
            onChange={(value) => {
              reason.current = value;
            }}
          />
        }
        isOpen={decision !== null}
        confirm={confirmDecision}
        cancel={() => setDecision(null)}
        onRequestClose={() => setDecision(null)}
      />
    </PageContent>
  );
};

Application.propTypes = {
  meetingType: PropTypes.oneOf(['cod', 'csp']).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  data: PropTypes.shape({
    meeting: PropTypes.shape({
      name: PropTypes.string,
      type: PropTypes.string,
      onsite: PropTypes.bool,
      date: PropTypes.string,
    }),
    application: PropTypes.shape({
      id: PropTypes.number,
      status: PropTypes.string,
      statusLabel: PropTypes.string,
      submittedAt: PropTypes.string,
    }),
    memberAssociation: PropTypes.shape({
      name: PropTypes.string,
      initials: PropTypes.string,
      country: PropTypes.string,
    }),
    applicant: PropTypes.shape({
      name: PropTypes.string,
      email: PropTypes.string,
    }),
    answers: PropTypes.shape({}),
    proxy: PropTypes.shape({
      name: PropTypes.string,
      country: PropTypes.string,
      approved: PropTypes.bool,
    }),
    statusHistory: PropTypes.arrayOf(PropTypes.shape({})),
    decidable: PropTypes.bool,
    awaitsChair: PropTypes.bool,
    verified: PropTypes.bool,
    flag: PropTypes.string,
    observer: PropTypes.bool,
    chairDecidable: PropTypes.bool,
    submittedBy: PropTypes.shape({
      name: PropTypes.string,
      organization: PropTypes.string,
      role: PropTypes.string,
    }),
  }),
  loadApplication: PropTypes.func.isRequired,
  decideApplication: PropTypes.func.isRequired,
  chairDecideApplication: PropTypes.func.isRequired,
  clearApplication: PropTypes.func.isRequired,
};

Application.defaultProps = {
  data: null,
};

const mapStateToProps = ({ ipma, user }) => ({
  data: ipma.meetings.application.data,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadApplication: (payload) => dispatch(loadIpmaMeetingApplicationCreator(payload)),
  decideApplication: (payload, history) =>
    dispatch(decideIpmaMeetingApplicationCreator(payload, history)),
  chairDecideApplication: (payload, history) =>
    dispatch(chairDecideIpmaMeetingApplicationCreator(payload, history)),
  clearApplication: () => dispatch(removeIpmaMeetingApplicationCreator()),
});

export default connect(mapStateToProps, mapDispatchToProps)(Application);
